import React, { useState, useEffect, useCallback } from 'react'
import { T, Spinner, Avatar, pct } from '../ui'
import {
  getNFLFeedback, postNFLFeedback, voteNFLFeedback,
  getNFLCommunityLeaderboard, getNFLWall,
} from '../api'
import { useMe, HandleBar, SignInPrompt, ResultBadge, ReactionBar, CommentsSheet, GuidelinesSheet, RemovalNotices } from '../community'

// The Community tab — three surfaces behind one sub-nav:
//   • Wall        — public logged picks + takes, each graded (receipts), with reactions/comments.
//   • Leaderboard — users ranked by their real graded record.
//   • Requests    — the feedback/upvote roadmap (users propose + vote; owner sets status).
// All read-open; posting/voting resolves identity from the paid code or free token.

const STATUS_CHIP = {
  open:     { fg: T.muted2, bg: T.card2, label: 'Open' },
  planned:  { fg: T.blue, bg: T.blueDim, label: 'Planned' },
  building: { fg: T.amber, bg: T.amberDim, label: 'Building' },
  shipped:  { fg: T.green, bg: T.greenDim, label: 'Shipped' },
  declined: { fg: T.faint, bg: T.card2, label: 'Declined' },
}
const CAT_LABEL = { feature: 'Feature', bug: 'Bug', data: 'Data', other: 'Other' }

// ── Requests (feedback roadmap) ───────────────────────────────────────────────
function RequestsView({ me, refreshMe }) {
  const [d, setD] = useState(null)
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState('votes')
  const [compose, setCompose] = useState(false)
  const load = useCallback(() => { getNFLFeedback(status, sort).then(setD) }, [status, sort])
  useEffect(() => { load() }, [load])
  const items = d?.items || []
  const counts = d?.status_counts || {}

  const vote = async (id) => {
    if (!me?.signed_in) { window.location.search = '?free=1'; return }
    try { await voteNFLFeedback(id); load() } catch (_) {}
  }
  return (
    <div>
      <HandleBar me={me} onChange={refreshMe} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {[['', 'All'], ['open', 'Open'], ['planned', 'Planned'], ['building', 'Building'], ['shipped', 'Shipped']].map(([k, l]) => {
          const on = status === k
          const n = k ? counts[k] : null
          return <button key={k} onClick={() => setStatus(k)} style={{ fontSize: 11.5, fontWeight: 800,
            border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : 'transparent',
            color: on ? T.green : T.muted, borderRadius: 999, padding: '5px 11px', cursor: 'pointer' }}>
            {l}{n != null ? ` ${n}` : ''}</button>
        })}
        <button onClick={() => setSort(s => s === 'votes' ? 'new' : 'votes')} style={{ marginLeft: 'auto',
          fontSize: 11.5, fontWeight: 700, border: `1px solid ${T.border}`, background: 'transparent',
          color: T.muted2, borderRadius: 999, padding: '5px 11px', cursor: 'pointer' }}>
          {sort === 'votes' ? '↓ Top voted' : '↓ Newest'}</button>
      </div>

      <button onClick={() => (me?.signed_in ? setCompose(true) : (window.location.search = '?free=1'))}
        style={{ width: '100%', padding: '11px 0', marginBottom: 14, borderRadius: 11, border: `1px dashed ${T.green}`,
          background: T.greenDim, color: T.green, fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
        ＋ Request a feature / report something</button>

      {d === null ? <Spinner /> : items.length === 0
        ? <div style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '20px 0' }}>
            Nothing here yet — be the first to post what you want built.</div>
        : items.map(f => {
          const sc = STATUS_CHIP[f.status] || STATUS_CHIP.open
          return (
            <div key={f.id} style={{ display: 'flex', gap: 11, background: T.card, border: `1px solid ${f.pinned ? T.green + '55' : T.border}`,
              borderRadius: 12, padding: '12px 13px', marginBottom: 9 }}>
              <button onClick={() => vote(f.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                minWidth: 44, border: `1px solid ${f.voted ? T.green : T.border2}`, background: f.voted ? T.greenDim : 'transparent',
                borderRadius: 9, padding: '6px 0', cursor: 'pointer', color: f.voted ? T.green : T.muted2 }}>
                <span style={{ fontSize: 13 }}>▲</span>
                <b style={{ fontSize: 14 }}>{f.votes}</b>
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800 }}>{f.title}</span>
                  {f.pinned && <span style={{ fontSize: 10, color: T.green }}>📌</span>}
                </div>
                {f.body && <div style={{ fontSize: 12, color: T.muted, marginTop: 3, lineHeight: 1.45 }}>{f.body}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: sc.fg, background: sc.bg, borderRadius: 6, padding: '2px 7px' }}>{sc.label}</span>
                  <span style={{ fontSize: 10.5, color: T.faint }}>{CAT_LABEL[f.category] || f.category}</span>
                  <span style={{ fontSize: 10.5, color: T.faint, marginLeft: 'auto' }}>@{f.handle}</span>
                </div>
              </div>
            </div>
          )
        })}
      {compose && <ComposeSheet onClose={() => setCompose(false)} onDone={() => { setCompose(false); load() }} />}
    </div>
  )
}

function ComposeSheet({ onClose, onDone }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [cat, setCat] = useState('feature')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async () => {
    if (!title.trim()) { setErr('Give it a title.'); return }
    setBusy(true); setErr('')
    try { await postNFLFeedback(title.trim(), body.trim(), cat); onDone() }
    catch (e) { setErr(e.message || 'Could not submit.'); setBusy(false) }
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 400,
      display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, margin: '0 auto',
        background: T.card2, borderRadius: '16px 16px 0 0', border: `1px solid ${T.border2}`, padding: '16px 16px 24px' }}>
        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 12 }}>What do you want built?</div>
        <input value={title} onChange={e => setTitle(e.target.value)} maxLength={120} placeholder="Short title"
          style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border2}`,
            borderRadius: 9, padding: '10px 12px', color: T.text, fontSize: 13, marginBottom: 9 }} />
        <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={2000} rows={3} placeholder="Details (optional)"
          style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border2}`,
            borderRadius: 9, padding: '10px 12px', color: T.text, fontSize: 12.5, resize: 'none', marginBottom: 10 }} />
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {Object.entries(CAT_LABEL).map(([k, l]) => (
            <button key={k} onClick={() => setCat(k)} style={{ flex: 1, padding: '7px 0', borderRadius: 8,
              border: `1px solid ${cat === k ? T.green : T.border2}`, background: cat === k ? T.greenDim : 'transparent',
              color: cat === k ? T.green : T.muted, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
        {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <button onClick={submit} disabled={busy} style={{ width: '100%', padding: '12px 0', borderRadius: 11,
          border: 'none', background: T.green, color: '#04140D', fontWeight: 900, fontSize: 13.5, cursor: 'pointer',
          opacity: busy ? 0.6 : 1 }}>{busy ? 'Posting…' : 'Post request'}</button>
      </div>
    </div>
  )
}

// ── Leaderboard ───────────────────────────────────────────────────────────────
function LeaderboardView({ season, week }) {
  const [scope, setScope] = useState('season')   // season | week
  const [d, setD] = useState(null)
  useEffect(() => { setD(null)
    getNFLCommunityLeaderboard(season, scope === 'week' ? week : 0, 3).then(setD)
  }, [season, week, scope])
  const leaders = d?.leaders || []
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['season', 'Season'], ['week', `Week ${week}`]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} style={{ flex: 1, padding: '8px 0', borderRadius: 9,
            border: `1px solid ${scope === k ? T.green : T.border2}`, background: scope === k ? T.greenDim : 'transparent',
            color: scope === k ? T.green : T.muted, fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 12, lineHeight: 1.45 }}>
        Ranked by win% across <b style={{ color: T.text }}>graded</b> logged picks (min 3). Log picks from any player card.</div>
      {d === null ? <Spinner /> : leaders.length === 0
        ? <div style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '24px 0' }}>
            No graded picks yet. Log 3+ and you'll appear here once they settle.</div>
        : leaders.map(r => (
          <div key={r.rank} style={{ display: 'flex', alignItems: 'center', gap: 11, background: T.card,
            border: `1px solid ${r.rank <= 3 ? T.green + '44' : T.border}`, borderRadius: 11, padding: '10px 13px', marginBottom: 7 }}>
            <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 15, width: 24,
              color: r.rank === 1 ? T.green : r.rank <= 3 ? T.mint : T.muted }}>{r.rank}</span>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }}>@{r.handle}</span>
            <span style={{ fontFamily: T.mono, fontSize: 12.5, color: T.muted2 }}>
              {r.wins}-{r.losses}{r.pushes ? `-${r.pushes}` : ''}</span>
            <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 14, width: 52, textAlign: 'right',
              color: r.win_pct >= 0.6 ? T.green : r.win_pct >= 0.5 ? T.mint : T.muted }}>
              {Math.round(r.win_pct * 100)}%</span>
          </div>
        ))}
    </div>
  )
}

// ── The Wall ───────────────────────────────────────────────────────────────────
function WallCard({ p, signedIn, onPlayer }) {
  const [comments, setComments] = useState(false)
  const sideLabel = p.prop_type === 'anytime_td' ? 'Anytime TD' : `${p.market} ${p.side === 'over' ? 'O' : 'U'}${p.line}`
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 13, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: T.green }}>@{p.handle}</span>
        <span style={{ fontSize: 10.5, color: T.faint }}>· Wk {p.week}</span>
        <span style={{ marginLeft: 'auto' }}><ResultBadge result={p.result} /></span>
      </div>
      <div onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, team: p.team, pos: p.pos })}
        style={{ cursor: onPlayer ? 'pointer' : 'default', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 900 }}>{p.name}</span>
        <span style={{ fontSize: 12.5, color: T.muted2, fontWeight: 700 }}>{sideLabel}</span>
        {p.disp_prob ? <span style={{ fontSize: 12, color: T.green, fontWeight: 800, fontFamily: T.mono }}>
          {Math.round(p.disp_prob * 100)}%</span> : null}
      </div>
      {p.take && <div style={{ fontSize: 12.5, color: T.text, marginTop: 7, lineHeight: 1.45 }}>{p.take}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <ReactionBar targetType="pick" targetId={p.id} counts={p.reactions} signedIn={signedIn} />
        <button onClick={() => setComments(true)} style={{ marginLeft: 'auto', background: 'none', border: 'none',
          color: T.muted2, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          💬 {p.comments || 0}</button>
      </div>
      {comments && <CommentsSheet targetType="pick" targetId={p.id} signedIn={signedIn} onClose={() => setComments(false)} />}
    </div>
  )
}

function WallView({ season, week, me, onPlayer }) {
  const [sort, setSort] = useState('new')
  const [scope, setScope] = useState('all')   // all | week
  const [d, setD] = useState(null)
  useEffect(() => { setD(null)
    getNFLWall(season, scope === 'week' ? week : 0, sort).then(setD)
  }, [season, week, sort, scope])
  const posts = d?.posts || []
  return (
    <div>
      <HandleBar me={me} onChange={() => {}} />
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['all', 'All weeks'], ['week', `Week ${week}`]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} style={{ fontSize: 11.5, fontWeight: 800,
            border: `1px solid ${scope === k ? T.green : T.border}`, background: scope === k ? T.greenDim : 'transparent',
            color: scope === k ? T.green : T.muted, borderRadius: 999, padding: '5px 11px', cursor: 'pointer' }}>{l}</button>
        ))}
        <button onClick={() => setSort(s => s === 'new' ? 'hot' : 'new')} style={{ marginLeft: 'auto',
          fontSize: 11.5, fontWeight: 700, border: `1px solid ${T.border}`, background: 'transparent',
          color: T.muted2, borderRadius: 999, padding: '5px 11px', cursor: 'pointer' }}>
          {sort === 'new' ? '↓ Newest' : '🔥 Hot'}</button>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 12, lineHeight: 1.45 }}>
        Public plays logged by the community — each shows its graded result once it settles. Open any player card and tap
        <b style={{ color: T.text }}> ＋ Log this pick</b> to post yours. Research only.</div>
      {!me?.signed_in && <div style={{ marginBottom: 12 }}><SignInPrompt compact /></div>}
      {d === null ? <Spinner /> : posts.length === 0
        ? <div style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '20px 0' }}>
            No public picks yet — be the first to post one.</div>
        : posts.map(p => <WallCard key={p.id} p={p} signedIn={!!me?.signed_in} onPlayer={onPlayer} />)}
    </div>
  )
}

// ── tab shell ───────────────────────────────────────────────────────────────
export default function CommunityTab({ season = 2026, week = 1, initial = 'wall', onPlayer }) {
  const [sub, setSub] = useState(initial)
  const [guide, setGuide] = useState(false)
  const { me, refresh } = useMe()
  const SUBS = [['wall', '🏟️ Wall'], ['leaderboard', '🏆 Leaderboard'], ['requests', '🗳️ Requests']]
  return (
    <div style={{ padding: '10px 14px 90px' }}>
      <div style={{ display: 'flex', gap: 2, background: T.card2, border: `1px solid ${T.border2}`,
        borderRadius: 999, padding: 3, marginBottom: 10 }}>
        {SUBS.map(([k, l]) => {
          const on = sub === k
          return <button key={k} onClick={() => setSub(k)} style={{ flex: 1, padding: '8px 0', borderRadius: 999,
            border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800,
            color: on ? '#04140D' : T.muted, background: on ? T.green : 'transparent' }}>{l}</button>
        })}
      </div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <button onClick={() => setGuide(true)} style={{ background: 'none', border: 'none', color: T.muted2,
          fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
          🛡️ Community guidelines</button>
      </div>
      {guide && <GuidelinesSheet onClose={() => setGuide(false)} />}
      <RemovalNotices signedIn={!!me?.signed_in} onSeeGuidelines={() => setGuide(true)} />
      {sub === 'wall' && <WallView season={season} week={week} me={me} onPlayer={onPlayer} />}
      {sub === 'leaderboard' && <LeaderboardView season={season} week={week} />}
      {sub === 'requests' && <RequestsView me={me} refreshMe={refresh} />}
    </div>
  )
}
